import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, X, MapPin, Search } from "lucide-react";
import { api, formatError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const EMPTY = {
  country: "", city: "", address: "",
  company: "", network: "",
  contact_name: "", phone: "", email: "",
  email_air_1: "", email_air_2: "",
  email_sea_1: "", email_sea_2: "",
  latitude: 20, longitude: 0,
};

export default function AgentLocations() {
  const { isAdmin } = useAuth();
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);

  const load = async () => {
    try {
      const { data } = await api.get("/agents");
      setAgents(data);
    } catch (e) { toast.error(formatError(e)); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;
    mapInstance.current = L.map(mapRef.current, {
      center: [20, 20], zoom: 2,
      scrollWheelZoom: true, touchZoom: true, doubleClickZoom: true,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap",
    }).addTo(mapInstance.current);
  }, []);

  useEffect(() => {
    if (!mapInstance.current) return;
    markersRef.current.forEach((m) => mapInstance.current.removeLayer(m));
    markersRef.current = [];
    agents.forEach((a) => {
      const m = L.marker([a.latitude, a.longitude]).addTo(mapInstance.current);
      m.bindPopup(`<b>${a.company || a.country}</b><br/>${a.network ? `Network: ${a.network}<br/>` : ""}${a.city ? a.city + ", " : ""}${a.country}`);
      m.on("click", () => setSelected(a));
      markersRef.current.push(m);
    });
  }, [agents]);

  const focusAgent = (a) => {
    setSelected(a);
    if (mapInstance.current) mapInstance.current.setView([a.latitude, a.longitude], 7);
  };

  const filtered = agents.filter((a) => {
    const q = search.toLowerCase();
    return !q || a.country.toLowerCase().includes(q) || (a.city || "").toLowerCase().includes(q) || (a.company || "").toLowerCase().includes(q) || (a.network || "").toLowerCase().includes(q);
  });

  const openAdd = () => { setEditingId(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (a) => { setEditingId(a.id); setForm({ ...EMPTY, ...a }); setShowForm(true); };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, latitude: parseFloat(form.latitude), longitude: parseFloat(form.longitude) };
      if (editingId) await api.put(`/agents/${editingId}`, payload);
      else await api.post("/agents", payload);
      toast.success(editingId ? "Agent updated" : "Agent added");
      setShowForm(false);
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete agent in ${a.country}?`)) return;
    try { await api.delete(`/agents/${a.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="agents-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#DC2A1C] font-semibold mb-1">Network</div>
          <h1 className="text-3xl font-black tracking-tight">Agent Locations</h1>
        </div>
        {isAdmin && (
          <button onClick={openAdd} className="tl-btn flex items-center gap-2" data-testid="add-agent-btn">
            <Plus className="w-4 h-4" /> Add Agent
          </button>
        )}
      </div>

      {/* Search bar */}
      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by country, city, company or network..."
          className="w-full bg-[#F8F9FA] border border-gray-200 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#DC2A1C]"
          data-testid="agents-search"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map */}
        <div className="lg:col-span-2 border border-gray-200 rounded-xl overflow-hidden h-[600px] bg-[#F8F9FA]">
          <div ref={mapRef} className="h-full w-full" data-testid="leaflet-map" />
        </div>

        {/* Agent list */}
        <div className="border border-gray-200 rounded-xl bg-white overflow-hidden flex flex-col h-[600px]">
          <div className="p-4 border-b border-gray-100 text-xs uppercase tracking-[0.15em] text-gray-500 font-semibold">
            {filtered.length} of {agents.length} Agents
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-gray-100" data-testid="agents-list">
            {filtered.map((a) => (
              <div
                key={a.id}
                className={`p-4 cursor-pointer hover:bg-[#F8F9FA] ${selected?.id === a.id ? "bg-[#F8F9FA] border-l-2 border-[#DC2A1C]" : ""}`}
                onClick={() => focusAgent(a)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold flex items-center gap-1.5 text-sm">
                      <MapPin className="w-3.5 h-3.5 text-[#DC2A1C] flex-shrink-0" />
                      {a.city ? `${a.city}, ` : ""}{a.country}
                    </div>
                    {a.company && (
                      <div className="text-xs font-semibold text-[#1A1A1A] mt-1">{a.company}</div>
                    )}
                    {a.network && (
                      <div className="text-[11px] text-gray-400 mt-0.5">
                        Network: <span className="font-semibold text-gray-600">{a.network}</span>
                      </div>
                    )}
                    {(a.email_air_1 || a.email_air_2) && (
                      <div className="mt-1.5">
                        <span className="text-[10px] uppercase tracking-wide font-semibold text-[#DC2A1C]">Air: </span>
                        {[a.email_air_1, a.email_air_2].filter(Boolean).map((em) => (
                          <a key={em} href={`mailto:${em}`} onClick={(e) => e.stopPropagation()} className="text-[11px] text-gray-500 hover:text-[#DC2A1C] block">{em}</a>
                        ))}
                      </div>
                    )}
                    {(a.email_sea_1 || a.email_sea_2) && (
                      <div className="mt-1">
                        <span className="text-[10px] uppercase tracking-wide font-semibold text-[#0F766E]">Sea: </span>
                        {[a.email_sea_1, a.email_sea_2].filter(Boolean).map((em) => (
                          <a key={em} href={`mailto:${em}`} onClick={(e) => e.stopPropagation()} className="text-[11px] text-gray-500 hover:text-[#0F766E] block">{em}</a>
                        ))}
                      </div>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={(e) => { e.stopPropagation(); openEdit(a); }} className="p-1.5 rounded hover:bg-gray-100" data-testid={`edit-agent-${a.id}`}><Edit2 className="w-3.5 h-3.5" /></button>
                      <button onClick={(e) => { e.stopPropagation(); remove(a); }} className="p-1.5 rounded hover:bg-red-50 text-red-600" data-testid={`delete-agent-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="p-6 text-sm text-gray-500 text-center">
                {search ? `No agents found for "${search}"` : "No agents yet."}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add / Edit modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={() => setShowForm(false)}>
          <form className="bg-white rounded-xl p-6 w-full max-w-2xl space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} onSubmit={onSubmit} data-testid="agent-form">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">{editingId ? "Edit" : "Add"} Agent</h3>
              <button type="button" onClick={() => setShowForm(false)}><X className="w-5 h-5" /></button>
            </div>

            <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#DC2A1C] mt-2">Location</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Country *" value={form.country} onChange={(v) => setForm({ ...form, country: v })} required />
              <Field label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
              <Field label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} full />
              <Field label="Latitude *" type="number" value={form.latitude} onChange={(v) => setForm({ ...form, latitude: v })} required />
              <Field label="Longitude *" type="number" value={form.longitude} onChange={(v) => setForm({ ...form, longitude: v })} required />
            </div>

            <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#DC2A1C] mt-2">Agent Info</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Company Name *" value={form.company} onChange={(v) => setForm({ ...form, company: v })} full />
              <Field label="Agent Network / Group" value={form.network} onChange={(v) => setForm({ ...form, network: v })} full placeholder="e.g. WCA, BNI, IATA" />
              <Field label="Contact Name *" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} required />
              <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            </div>

            <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#DC2A1C] mt-2">Air Emails (max 2)</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Air Email 1" value={form.email_air_1} onChange={(v) => setForm({ ...form, email_air_1: v })} type="email" />
              <Field label="Air Email 2" value={form.email_air_2} onChange={(v) => setForm({ ...form, email_air_2: v })} type="email" />
            </div>

            <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#0F766E] mt-2">Sea Emails (max 2)</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Sea Email 1" value={form.email_sea_1} onChange={(v) => setForm({ ...form, email_sea_1: v })} type="email" />
              <Field label="Sea Email 2" value={form.email_sea_2} onChange={(v) => setForm({ ...form, email_sea_2: v })} type="email" />
            </div>

            <button type="submit" className="tl-btn w-full mt-2" data-testid="agent-form-submit">{editingId ? "Update Agent" : "Add Agent"}</button>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, full, placeholder }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="block text-[11px] uppercase tracking-[0.12em] font-semibold text-gray-500 mb-1">{label}</label>
      <input
        type={type} value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required} step="any"
        placeholder={placeholder}
        className="w-full bg-[#F8F9FA] border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#DC2A1C]"
      />
    </div>
  );
}
